import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { Lock, Mail, Factory } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Pulsating Reactor Core for Login
const LoginReactorCore = () => {
  return (
    <div className="relative w-40 h-40 flex items-center justify-center mx-auto mb-8">
      {/* Outer rotating ring */}
      <div className="absolute inset-0 rounded-full border-2 border-cyan-500/30 animate-[spin_20s_linear_infinite]">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1 w-2 h-2 bg-cyan-400 rounded-full" />
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1 w-2 h-2 bg-cyan-400 rounded-full" />
      </div>
      
      {/* Middle counter-rotating ring */}
      <div className="absolute inset-4 rounded-full border border-cyan-500/20 animate-[spin_15s_linear_infinite_reverse]">
        <div className="absolute top-1/2 left-0 -translate-y-1/2 -translate-x-1 w-1.5 h-1.5 bg-cyan-400/60 rounded-full" />
        <div className="absolute top-1/2 right-0 -translate-y-1/2 translate-x-1 w-1.5 h-1.5 bg-cyan-400/60 rounded-full" />
      </div>
      
      {/* Inner pulsating core */}
      <div className="absolute inset-6 rounded-full bg-gradient-to-br from-cyan-950/80 to-slate-900/80 border border-cyan-500/40 animate-[pulse_2s_ease-in-out_infinite] shadow-[0_0_30px_rgba(0,240,255,0.3),inset_0_0_20px_rgba(0,240,255,0.1)]">
        <div className="absolute inset-0 flex items-center justify-center">
          <img 
            src="/hotforge-logo.png" 
            alt="Hotforge" 
            className="w-20 h-20 object-contain opacity-90"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
          <div className="hidden items-center justify-center text-cyan-400 font-black text-2xl tracking-tighter">
            HF
          </div>
        </div>
      </div>
      
      {/* Glow effect */}
      <div className="absolute inset-0 rounded-full bg-cyan-500/5 blur-xl animate-[pulse_2s_ease-in-out_infinite]" />
    </div>
  );
};

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/login`, formData);
      login(response.data);
      toast.success(`Welcome back, ${response.data.name}!`);
      navigate('/');
    } catch (error) {
      const message = error.response?.data?.detail || 'Login failed';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center p-4 overflow-hidden relative">
      {/* Honeycomb mesh background */}
      <div className="absolute inset-0 overflow-hidden">
        <svg className="absolute inset-0 w-full h-full opacity-40" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="honeycomb-login" x="0" y="0" width="56" height="100" patternUnits="userSpaceOnUse" patternTransform="scale(1.5)">
              <path 
                d="M28 0 L56 16.67 L56 50 L28 66.67 L0 50 L0 16.67 Z M28 100 L56 83.33 L56 116.67 L28 133.33 L0 116.67 L0 83.33 Z M84 50 L112 66.67 L112 100 L84 116.67 L56 100 L56 66.67 Z M-28 50 L0 66.67 L0 100 L-28 116.67 L-56 100 L-56 66.67 Z" 
                fill="none" 
                stroke="rgba(0, 200, 255, 0.6)" 
                strokeWidth="1.5"
              />
            </pattern>
            <radialGradient id="honeycomb-fade-login" cx="50%" cy="50%" r="50%" fx="50%" fy="50%">
              <stop offset="0%" stopColor="white" stopOpacity="0" />
              <stop offset="35%" stopColor="white" stopOpacity="0" />
              <stop offset="60%" stopColor="white" stopOpacity="0.5" />
              <stop offset="100%" stopColor="white" stopOpacity="1" />
            </radialGradient>
            <mask id="honeycomb-mask-login">
              <rect width="100%" height="100%" fill="url(#honeycomb-fade-login)" />
            </mask>
          </defs>
          <rect width="100%" height="100%" fill="url(#honeycomb-login)" mask="url(#honeycomb-mask-login)" />
        </svg>
      </div>
      
      {/* Background radial glow */}
      <div 
        className="absolute inset-0 opacity-20"
        style={{
          backgroundImage: `radial-gradient(circle at center, rgba(0, 240, 255, 0.15) 0%, transparent 50%)`,
          backgroundSize: '100% 100%'
        }}
      />

      {/* Status indicator - top */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-slate-950/90 backdrop-blur-sm border-b border-cyan-500/20">
        <div className="container mx-auto px-6 py-2 flex items-center justify-center">
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <div className="w-2 h-2 rounded-full bg-cyan-500 animate-pulse" />
            <span className="uppercase tracking-wider">Factory Command Center</span>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="w-full max-w-md relative z-10">
        {/* Reactor Core Logo */}
        <LoginReactorCore />

        {/* Title */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-black text-white uppercase tracking-tight">
            Hot<span className="text-cyan-400">forge</span>
          </h1>
          <p className="text-slate-500 text-xs uppercase tracking-widest mt-2">
            Manufacturing Workflow Management
          </p>
        </div>

        {/* Login Form Card with Glassmorphism */}
        <div className="bg-slate-900/60 backdrop-blur-xl border border-cyan-500/20 rounded-2xl p-8 shadow-[0_0_40px_rgba(0,0,0,0.5)]">
          <div className="flex items-center justify-center gap-2 mb-6">
            <Factory className="w-5 h-5 text-cyan-400" />
            <h2 className="text-lg font-bold text-white uppercase tracking-wide">
              System Access
            </h2>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label htmlFor="email" className="text-slate-400 uppercase tracking-wider text-xs mb-2 flex items-center gap-2">
                <Mail className="w-3.5 h-3.5 text-cyan-400" />
                Email Address
              </Label>
              <Input
                id="email"
                type="email"
                data-testid="login-email-input"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="bg-slate-950/50 border-slate-700 focus:border-cyan-500 focus:ring-cyan-500/20 text-white placeholder:text-slate-600 h-11"
                placeholder="your@email.com"
                required
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-slate-400 uppercase tracking-wider text-xs mb-2 flex items-center gap-2">
                <Lock className="w-3.5 h-3.5 text-cyan-400" />
                Password
              </Label>
              <Input
                id="password"
                type="password"
                data-testid="login-password-input"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="bg-slate-950/50 border-slate-700 focus:border-cyan-500 focus:ring-cyan-500/20 text-white placeholder:text-slate-600 h-11"
                placeholder="••••••••"
                required
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-btn"
              className="w-full bg-cyan-600 hover:bg-cyan-500 text-white h-12 font-bold uppercase tracking-wide transition-all duration-300 shadow-[0_0_20px_rgba(0,240,255,0.3)] hover:shadow-[0_0_30px_rgba(0,240,255,0.5)] border border-cyan-400/30"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Authenticating...
                </span>
              ) : (
                'Initialize Session'
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-slate-500 text-sm">
              New operator?{' '}
              <Link 
                to="/register" 
                className="text-cyan-400 hover:text-cyan-300 font-bold uppercase text-xs tracking-wider transition-colors"
                data-testid="register-link"
              >
                Request Access
              </Link>
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-8">
          <p className="text-slate-600 text-xs uppercase tracking-widest">
            Hotforge AI-Workflow © {new Date().getFullYear()}
          </p>
          <div className="flex items-center justify-center gap-2 mt-2">
            <div className="w-1.5 h-1.5 rounded-full bg-green-500" />
            <span className="text-green-500 text-[10px] uppercase tracking-wider font-medium">System Online</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
